import React, { useEffect } from 'react';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useMutation } from '@apollo/client';
import useClassroomStore from 'graph/state/query-hooks/use-classrooms';
import Link from '@mui/material/Link';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import ClassroomCard from './components/ClassroomCard';
import HomeTourModal from '../HomeTourModal';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import Config from '../../../config';
import Button from '@mui/material/Button';
import { parseQueryParams } from '@lwtears/lwt-common-frontend/lib/@common/util/query-params-util';
import useTourStore from 'graph/state/query-hooks/use-tour';
import { Settings } from 'types';
import SAVE_TOUR_SETTING from 'graph/mutations/tourSettings/save-tour-setting';

const useStyles = makeStyles((theme: Theme) => {
  return {
    pliRedirectButtonLink: {
      color: 'white !important',
      textDecoration: 'none !important',
    },
    noClassesContainer: {
      maxWidth: '50%',
      marginTop: theme.spacing(4),
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      flexDirection: 'column',
      margin: 'auto',
      textAlign: 'center',
    },
    dFlex: {
      display: 'flex',
    },
    pb2: {
      paddingBottom: theme.spacing(2),
    },
    pb4: {
      paddingBottom: theme.spacing(4),
    },
    mainContainer: {
      paddingLeft: theme.spacing(30),
      paddingRight: theme.spacing(30),
      paddingBottom: theme.spacing(10),
    },
    pageHeader: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(2),
      alignItems: 'flex-end',
    },
    spacer: {
      marginLeft: theme.spacing(4),
      marginRight: theme.spacing(4),
      height: '40px',
    },
    label: {
      ...theme.typography.subtitle1,
    },
    panelLink: {
      width: '100%',
      padding: theme.spacing(2),
      textDecoration: 'none !important',
    },
    pliButton: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
      border: '2px solid ' + theme.palette.secondary.main,
      borderRadius: '4px',
    },
    pliSection: {
      marginLeft: theme.spacing(30),
      marginRight: theme.spacing(30),
      backgroundColor: 'white',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(8),
      border: `1px solid ${(theme.palette as any).grey[600]}`,
      borderRadius: '4px',
      boxShadow: '0 1px 10px 2px rgb(0 0 0 / 20%)',
    },
    pliNoDecoration: {
      textDecoration: 'none !important',
      color: theme.palette.secondary.main + ' !important',
    },
    secondaryLink: {
      textDecoration: 'none !important',
      color: theme.palette.black.main + ' !important',
      paddingRight: theme.spacing(4),
      fontFamily: 'HWT Futura',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline !important',
      },
    },
    p4: {
      padding: theme.spacing(4),
    },
    p4r: {
      paddingRight: theme.spacing(4),
    },
  };
});

const t = (key) => translate('ui', key);

const Home = (): React.ReactElement => {
  const navigate = useNavigate();
  const [classrooms] = useClassroomStore({
    whereUser: { id: Number(sessionStorage.getItem('userID')) },
    whereLicense: { productFamily: [{ code: 'PREKITT' }, { code: 'HWT' }] },
    whereClassroom: { archived: false },
  });

  const [tourStore] = useTourStore(Settings.WELCOME);
  const [saveTourSetting] = useMutation(SAVE_TOUR_SETTING);

  const query = parseQueryParams();

  const [, { openModal, closeModal }] = useModal();
  const classes = useStyles();

  const saveViewedAndCloseModal = () => {
    if (tourStore.tour) {
      for (const stepSetting of tourStore.tour.tourSteps) {
        saveTourSetting({
          variables: {
            data: {
              userId: Number(sessionStorage.getItem('userID')),
              toursSettings: [
                {
                  tourId: tourStore.tour.id,
                  tourStepSettings: [{ tourStepId: stepSetting.id, viewed: true }],
                },
              ],
            },
          },
        });
      }
    }
    closeModal();
    navigate('/home');
  };

  const displayHomeTourModal = () => {
    openModal({
      body: <HomeTourModal closeModal={saveViewedAndCloseModal} />,
    });
  };

  useEffect(() => {
    if (query?.tour === 'true') displayHomeTourModal();
  }, [tourStore.tour]);

  return (
    <>
      <div className={classes.mainContainer} style={{ flexGrow: 1 }}>
        <Grid container>
          <Grid container item xs={12} className={classes.pageHeader}>
            <Grid item xs={6}>
              <Typography variant="h1">{t('home.classes')}</Typography>
            </Grid>
            <Grid
              item
              xs={6}
              className={classes.dFlex}
              alignItems="flex-end"
              justifyContent="flex-end"
            >
              <div></div>
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        {classrooms?.classrooms && (
          <Grid container>
            {classrooms.classrooms?.length === 0 && (
              <div className={classes.noClassesContainer}>
                <Typography variant="h1">{t('home.noClassroomsTitle')}</Typography>
                <br />
                <Typography variant="body1">{t('home.noClassroomsText')}</Typography>
                <br />
                <Link
                  href={`${Config.pliUrl}/classrooms?action=create-class-view`}
                  rel="noopener noreferrer"
                  target="_blank"
                  variant="body1"
                  className={classes.secondaryLink}
                >
                  <b>{t('home.noClassroomsLeave')}</b>
                </Link>
              </div>
            )}
            {classrooms.classrooms?.map((classroom, index) => (
              <Grid key={index} container item xs={4}>
                <Link
                  component={RouterLink}
                  to={`/class/${classroom.id}`}
                  className={classes.panelLink}
                >
                  <ClassroomCard classroom={classroom} />
                </Link>
              </Grid>
            ))}
          </Grid>
        )}
      </div>
      {classrooms?.classrooms && classrooms.classrooms.length > 0 && (
        <section className={classes.pliSection}>
          <Grid container item xs={12} justifyContent="space-between">
            <Grid item xs={6}>
              <Typography variant="body1" align="left" className={classes.p4}>
                {t('home.pliLinkDescriptor')}
              </Typography>
            </Grid>
            <Grid
              item
              xs={6}
              container
              alignItems="center"
              justifyContent="flex-end"
              className={classes.p4r}
            >
              <Typography variant="body1" align="right">
                <Button className={classes.pliButton}>
                  <Link
                    href={`${Config.pliUrl}/classrooms?action=create-class-view`}
                    rel="noopener noreferrer"
                    target="_blank"
                    className={classes.pliNoDecoration}
                  >
                    {t('home.pliLinkButton')}
                  </Link>
                </Button>
              </Typography>
            </Grid>
          </Grid>
        </section>
      )}
    </>
  );
};

export default Home;
